import React from "react";
import { CustomIcon } from "../../../components/Icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import classNames from "classnames";
import { campIndicatorHintKeys } from "./data";
import { formatCompactNumber } from "../../../i18n/formatters";
import { useAppTranslation } from "../../../i18n/useAppTranslation";

type Props = {
  value: number;
  className?: string;
};
export const milestones = [0, 100000, 300000, 900000, 2700000, 8100000];

const CampIndicator = ({ value, className }: Props) => {
  const { t } = useAppTranslation(["barracks"]);
  const anchors = {
    0: {
      describe: t(campIndicatorHintKeys[0]),
      active: true,
      title: "0",
    },
    100: {
      describe: t(campIndicatorHintKeys[100000]),
      active: value >= 100000,
      title: "100K",
    },
    300: {
      describe: t(campIndicatorHintKeys[300000]),
      active: value >= 300000,
      title: "300K",
    },
    900: {
      describe: t(campIndicatorHintKeys[900000]),
      active: value >= 900000,
      title: "900K",
    },
    2700: {
      describe: t(campIndicatorHintKeys[2700000]),
      active: value >= 2700000,
      title: "2.7M",
    },
    8100: {
      describe: t(campIndicatorHintKeys[8100000]),
      active: value >= 8100000,
      title: "8.1M",
    },
  };

  // Determine the segment
  const findSegment = () => {
    for (let i = 0; i < milestones.length - 1; i++) {
      if (value >= milestones[i] && value <= milestones[i + 1]) {
        return i; // Return the current segment index
      }
    }
    return -1; // Out of range
  };

  const segmentIndex = findSegment();
  // Calculate progress within the segment
  const calculateProgress = (): number => {
    if (segmentIndex === -1) return 0; // Out of range, no progress
    const start = milestones[segmentIndex];
    const end = milestones[segmentIndex + 1];
    const progress = ((value - start) / (end - start)) * 100;
    return Math.min(Math.max(progress, 0), 100); // Clamp progress to 0-100%
  };

  const overallProgress = (): number => {
    if (segmentIndex === -1) return 0; // Out of range
    const segmentProgress = calculateProgress();
    const totalSegments = milestones.length - 1;
    const progressPerSegment = 100 / totalSegments;
    return (
      segmentIndex * progressPerSegment +
      (segmentProgress * progressPerSegment) / 100
    );
  };

  return (
    <div
      className={classNames(
        "p-relative d-flex align-items-center justify-content-between indicator-container",
        className
      )}
    >
      <div
        style={{ width: `${overallProgress()}%` }}
        className="indicator-value-progress"
      ></div>

      <div className="indicator-pin text-center">
        <div className=" ">
          <span className="light-yellow text-shadow">
            {t("barracks:camp.indicator.valueLabel")}:
          </span>{" "}
          <span className="text-stroke  text-white bold fs-big">
            {formatCompactNumber(value)}
          </span>
        </div>
      </div>
      {Object.keys(anchors).map((key, index) => {
        return (
          <div
            key={key}
            style={{
              marginLeft: index === 0 ? -10 : 0,
              marginRight: index === Object.keys(anchors).length - 1 ? -10 : 0,
            }}
            className="p-relative d-flex align-items-center justify-content-between"
          >
            <OverlayTrigger
              overlay={
                <Tooltip className="fs-tiny">{anchors[key].describe}</Tooltip>
              }
            >
              <div className="indicator-details">
                <span className="d-flex align-items-center">
                  {anchors[key].title}{" "}
                  <CustomIcon
                    icon={"info"}
                    className="ms-1"
                    width={12}
                    height={12}
                  />
                </span>
              </div>
            </OverlayTrigger>
            <div className="p-relative d-flex align-items-center justify-content-between">
              <div className="">
                <div
                  className="indicator"
                  style={{
                    width: `${(+key / 100) * 100}%`,
                  }}
                >
                  <div className="indicator-dot">
                    {anchors[key].active ? (
                      <img
                        src="/assets/images/icons/active-dot.png"
                        width={28}
                        height={28}
                      />
                    ) : (
                      index
                    )}
                  </div>
                </div>
              </div>
              {/* <div className="p-relative d-flex align-items-center justify-content-between">
                  <div className="fs-big text-white">{anchors[key].title}</div>
                  <div className="fs-sm text-white">
                    {anchors[key].describe}
                  </div>
                </div> */}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CampIndicator;
